import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { enqueue, flush, routeLabel } from '../lib/activityTracker';

const IDLE_MS = 10 * 60 * 1000; // 10 minutes

// Traçabilité + déconnexion auto (sauf admin). Composant invisible monté une seule fois.
const ActivityTracker = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const idleTimer = useRef(null);

  // Traçage des changements de page
  useEffect(() => {
    if (!user) return;
    enqueue({ action: 'navigate', label: routeLabel(location.pathname), page: location.pathname });
  }, [location.pathname, user]);

  // Traçage des clics sur les éléments importants (boutons / liens / testids)
  // IMPORTANT iOS/iPad: sur les appareils TACTILES on n'attache PAS l'écouteur global
  // de clics, pour ne JAMAIS risquer d'interférer avec l'ouverture native du sélecteur
  // de photos (input type=file) sur Safari iOS. Navigation + actions métier restent tracées.
  useEffect(() => {
    if (!user) return;
    const isTouchDevice =
      typeof window !== 'undefined' &&
      (('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0);
    if (isTouchDevice) return;
    const onClick = (e) => {
      try {
        // Ne jamais toucher/observer les contrôles de fichier ni leurs labels
        if (e.target.closest('input, label, [type="file"]')) return;
        const el = e.target.closest('button, a, [role="button"], [data-testid]');
        if (!el) return;
        let label =
          (el.innerText || '').trim().replace(/\s+/g, ' ').slice(0, 60) ||
          el.getAttribute('aria-label') ||
          el.getAttribute('data-testid') ||
          el.getAttribute('title');
        if (!label) return;
        enqueue({ action: 'click', label, page: location.pathname });
      } catch (_) { /* noop */ }
    };
    document.addEventListener('click', onClick, { capture: false, passive: true });
    return () => document.removeEventListener('click', onClick, { capture: false, passive: true });
  }, [user, location.pathname]);

  // Envoi périodique + à la fermeture / mise en arrière-plan
  useEffect(() => {
    const interval = setInterval(() => flush(false), 5000);
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush(true);
    };
    const onPageHide = () => flush(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  // Déconnexion automatique après 10 min d'inactivité (jamais pour l'admin)
  useEffect(() => {
    if (!user || user.role === 'admin') return;

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(async () => {
        enqueue({
          action: 'logout',
          label: 'Déconnexion automatique (inactivité 10 min)',
          page: window.location.pathname,
          meta: { reason: 'inactivity' },
        });
        flush(false);
        try { await logout(true, 'inactivity'); } catch (_) { /* noop */ }
      }, IDLE_MS);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, resetTimer));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  return null;
};

export default ActivityTracker;
